import React, { useContext } from "react";
import { useFeaturesContextQuery } from "__generated__/graphql";

export interface IFeaturesContext {
  enabledFeatures?: string[];
  enabledFeaturesSet?: Set<string>;
}

const INITIAL_STATE: IFeaturesContext = {
  enabledFeatures: [],
  enabledFeaturesSet: new Set(),
};

export const FeaturesContext =
  React.createContext<IFeaturesContext>(INITIAL_STATE);

/**
 * @deprecated Use useFeatureFlags instead
 */
export const useFeatures = () => useContext(FeaturesContext);

export const useFeatureFlags = (...featureKeys: string[]) => {
  const context = useContext(FeaturesContext);
  if (!context) {
    throw new Error("useFeatureFlags must be used within a FeaturesProvider");
  }

  const { enabledFeatures = [], enabledFeaturesSet } = context;

  // Only create a new Set if enabledFeaturesSet is not provided - mainly for testing
  const flagsSet = enabledFeaturesSet || new Set(enabledFeatures);

  if (featureKeys.length === 0) {
    return Object.fromEntries(
      Array.from(flagsSet).map((key) => [key, true]),
    ) as Record<string, boolean>;
  }

  const result: Record<string, boolean> = {};
  for (const key of featureKeys) {
    result[key] = flagsSet.has(key);
  }
  return result;
};

export const FeaturesConsumer = FeaturesContext.Consumer;

export interface FeaturesProviderProps {
  children: React.ReactNode;
  defaultEnabledFeatures?: string[];
}

export const FeaturesProvider = ({
  children,
  defaultEnabledFeatures = [],
}: FeaturesProviderProps) => {
  const { data } = useFeaturesContextQuery();

  const enabledFeatures = [...defaultEnabledFeatures];

  if (data?.features && data.features.length > 0) {
    data.features
      .filter((feature) => feature.enabled === true)
      .forEach((feature) => enabledFeatures.push(feature.key));
  }

  if (data?.enabledModules) {
    Object.keys(data.enabledModules).forEach((key) => {
      if (key !== "__typename" && data.enabledModules[key]) {
        enabledFeatures.push(key);
      }
    });
  }

  const enabledFeaturesSet = new Set(enabledFeatures);

  return (
    <FeaturesContext.Provider value={{ enabledFeatures, enabledFeaturesSet }}>
      {children}
    </FeaturesContext.Provider>
  );
};
